/* eslint-disable custom-rules/modal-and-drawer-requires-title */
import { Button } from '@uhg-abyss/web/ui/Button';
import { Drawer } from '@uhg-abyss/web/ui/Drawer';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import { Layout } from '@uhg-abyss/web/ui/Layout';
import { Text } from '@uhg-abyss/web/ui/Text';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useSuppressNiceMembers } from '../../hooks/useSuppressNiceMembers';
import { returnSuppressFlag } from '../../utils/featureSuppress';
import { getCurrentMember } from '../../utils/user.utils';
import { ConstantsLagoon } from '../ConstantsLagoon';
import { RadioGroup } from '../RadioGroup';
import { handleLinkAndModalTrack } from '../Utils/adobeTrackUtils';
import { isMnrCosmosMember } from '../Utils/memberUtils/memberUtils';
import { Radio, filteredOptions } from './SortByUtil';

type Props = {
  content?: string;
  bestMatchTitle?: string;
  smartChoiceTitle?: string;
  defaultValue?: string | number | boolean;
  title: string;
  name: string;
  size: string;
  color?: string;
  icon?: string;
  variant?: string;
  model: string;
  options: Radio[];
  onApply: (value: string) => void;
};

export const SortByMobile = ({
  content,
  bestMatchTitle,
  smartChoiceTitle,
  defaultValue,
  title,
  name,
  size,
  color = '$core.color.brand.100',
  icon = 'sort',
  variant = 'outline',
  model,
  options,
  onApply,
}: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const { t } = useTranslation();

  const currentMember = getCurrentMember();
  const isMnr = isMnrCosmosMember(
    currentMember?.lineOfBusiness,
    currentMember?.population
  );
  const suppressPatientReviews = returnSuppressFlag(
    ConstantsLagoon.FEATURE_SUPPRESSION_FLAGS.PATIENT_REVIEWS
  );
  const suppressPremiumCare = returnSuppressFlag(
    ConstantsLagoon.FEATURE_SUPPRESSION_FLAGS.PREMIUM_CARE
  );
  const { suppressForNiceMembers: suppressPremiumCareForNiceMembers } =
    useSuppressNiceMembers();

  const filOptions = {
    options,
    suppressPatientReviews,
    suppressPremiumCare:
      suppressPremiumCare || suppressPremiumCareForNiceMembers,
    population: currentMember?.population,
    isMnr,
    isCns: currentMember?.isCnSMember,
    isIfp: currentMember?.isIFPMember,
  };

  const handOnApply = (value: string) => {
    if (value) {
      onApply(value);
    }
    setIsOpen(false);
  };

  const handleOnClick = () => {
    const linkName: string = title;
    const linkLocation: string = 'body:sort by';
    handleLinkAndModalTrack(linkName, linkLocation, title);
    setIsOpen(true);
  };

  return (
    <React.Fragment>
      {isOpen ? (
        <Drawer
          css={{
            'abyss-modal-base-content-container': {
              borderRadius: '20px 20px 0px 0px',
              padding: '$md',
              height: 'auto !important',
              '.abyss-radio-root': {
                '&:hover': {
                  backgroundColor: '$core.color.aqua.15',
                },
              },
            },
            'abyss-modal-close-button': {
              top: '12px',
              right: '12px',
            },
            'abyss-modal-header-container': {
              padding: '0',
              marginBottom: '$md',
            },
            'abyss-modal-content-title': {
              fontFamily: '$primary !important',
              fontSize: 'medium !important',
              fontWeight: 'bold !important',
              lineHeight: '24px !important',
              color: '$core.color.neutral.80 !important',
              padding: '0',
            },
          }}
          header={title}
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
          position="right"
          size={'fit-content'}
        >
          <RadioGroup
            bestMatchTitle={bestMatchTitle}
            content={content}
            defaultValue={defaultValue}
            labelFontSize="16px"
            locationForAnalytics={`modal:${title}`}
            model={model}
            onApply={handOnApply}
            options={filteredOptions(filOptions)}
            smartChoiceTitle={smartChoiceTitle}
            submitBtnName={t('Sort') ?? 'Sort'}
          />
        </Drawer>
      ) : null}
      <Button
        css={{
          'abyss-button-root': {
            border: 'solid 2px $core.color.brand.100',
            '&:active, &:focus, &:hover': {
              backgroundColor: '#D0F1F5',
            },
          },
        }}
        data-auto-testid="result-sort-mobile"
        data-testid="result-sort-mobile"
        onClick={handleOnClick}
        size={size}
        variant={variant}
      >
        <Layout.Group>
          <IconSymbol color={color} icon={icon} />
          <Text color={color} fontWeight="medium">
            {name}
          </Text>
        </Layout.Group>
      </Button>
    </React.Fragment>
  );
};
